import React, { useContext, useEffect, useState } from "react";
import MarketDepth from "../../../screens/OrdersPage/components/MarketDepth";
import WebsocketWrapperContext from "../../../context/WebsocketWrapperContext";
import { useNavigate } from "react-router-dom";
import OcIcon from "../../../images/Builder/option_chain.svg";
import { useTheme } from "../../../context/ThemeContext";
import DeleteIcon from "../../../images/icons/delete.svg";

const InstrumentModal = ({
  isOpen,
  onClose,
  instrument,
  allSymbols,
  onDelete,
  onBuy, 
  onSell,
  isIndex,
}) => {
  const { theme } = useTheme();
  const [isMarketDepthExpanded, setIsMarketDepthExpanded] = useState(true);
  const { symbolData } = useContext(WebsocketWrapperContext);
  const [isOptionchainVisible, setIsOptionchainVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!instrument || !allSymbols) return;

    const match = allSymbols.find(
      (symbol) => symbol.scrip === instrument.symbol
    );

    setIsOptionchainVisible(!!match);
  }, [instrument, allSymbols]);

  if (!isOpen || !instrument) return null;

  const LtpWidget = ({ code }) => {
    const ltpPrice = symbolData[code]?.ltp || 0;
    const pctChange = symbolData[code]?.change_per || 0;
    const changeColor = pctChange >= 0 ? "text-p150" : "text-[#B01431]";
    return (
      <div className="flex items-center">
        <span className="text-xl font-bold text-p300">{ltpPrice}</span>
        <span className={`ml-2 text-xs font-medium ${changeColor}`}>
          {pctChange >= 0 ? "+" : ""}
          {pctChange}%
        </span>
      </div>
    );
  };

  // Add handlers for buy/sell button clicks
  const handleBuyClick = () => {
    if (onBuy && !isIndex) {
      onBuy(instrument);
    }
  };

  const handleSellClick = () => {
    if (onSell && !isIndex) {
      onSell(instrument);
    }
  };

  return (
    <div className="modal modal-open">
      <div className="modal-box no-scrollbar bg-modalBg max-w-md p-0 rounded-lg">
        {/* Header */}
        <div className="sticky top-0 z-10 p-4 border-b border-n100">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-medium text-p300">
                {instrument.symbol}
              </h3>
              <div className="mt-1">
                <LtpWidget code={instrument.code} />
              </div>
            </div>

            <div className="flex items-center">
              <div className="bg-n100 rounded-sm px-2 py-1">
                <div className="text-xs font-medium text-n700">
                  {instrument.exchange}
                </div>
              </div>
              <button
                className="ml-4 text-n400 hover:text-n700"
                onClick={onClose}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 py-4">
          <button
            className="flex w-full justify-between items-center focus:outline-none"
            onClick={() => setIsMarketDepthExpanded(!isMarketDepthExpanded)}>
            <h3 className="text-base font-semibold text-p300">Market Depth</h3>
            <span className="text-n400">
              {isMarketDepthExpanded ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </span>
          </button>

          {isMarketDepthExpanded && (
            <div className="mt-4">
              <MarketDepth symbolCode={instrument.code} />
            </div>
          )}
        </div>

     
        <div className="px-4 mt-4">
          <button
            className="w-full flex items-center justify-center gap-3 h-9 font-medium text-sm btn-op mb-6 rounded-md"
            onClick={() => onDelete(instrument)}>
            <img
              src={DeleteIcon}
              alt="delete-icon"
              width={16}
              height={16}
              style={{
                filter: theme === "dark" ? "brightness(0) invert(1)" : "none",
              }}
            />
            Delete
          </button>
        </div>

        {/* Option Chain Button */}
        {isOptionchainVisible && (
          <div className="px-4 mt-4">
            <button
              className="w-full flex items-center justify-center gap-3 h-9 font-medium text-sm btn-op mb-6 rounded-md"
              onClick={() => {
                navigate(`/optionchain/${instrument.symbol}`);
              }}>
              <img
                src={OcIcon}
                alt="option-chain-icon"
                width={16}
                height={16}
                style={{
                  filter: theme === "dark" ? "brightness(0) invert(1)" : "none",
                }}
              />
              Option Chain
            </button>
          </div>
        )}

        
        <div className="w-full flex items-center justify-center gap-3 h-9 font-medium text-sm mb-6 rounded-md px-4 py-4">
          <button
            className={`flex-1 py-2 px-4 rounded-md ${
              isIndex
                ? "bg-n300 text-white cursor-not-allowed"
                : "bg-p150 text-white hover:bg-p150/90 transition-colors"
            }`}
            disabled={isIndex}
            onClick={handleBuyClick}>
            BUY
          </button>
          <button
            className={`flex-1 py-2 px-4 rounded-md ${
              isIndex
                ? "bg-n300 text-white cursor-not-allowed"
                : "bg-red text-white hover:bg-red/90 transition-colors"
            }`}
            disabled={isIndex}
            onClick={handleSellClick}>
            SELL
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstrumentModal;
